import React, { useState } from 'react';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * "Need help?" ticket modal.
 *
 * Posts to https://support.sense-ads.io via a 10-minute scoped token
 * minted by our own /api/support/token endpoint. The browser never
 * sees the shared SUPPORT_EXTERNAL_SECRET.
 *
 * Topic is a UX-level convenience: the Support API accepts category_id
 * as a UUID, but there's no public endpoint to list them. So we just
 * pre-pend the chosen topic to the subject — "[Billing] Cannot top up"
 * — and the support team can route from there.
 */
// `value` is prepended to the ticket subject and is what the support team routes
// on, so it stays English; only labelKey is interface text.
const TOPICS = [
  { value: 'Account & Access', labelKey: 'support.topicAccess' },
  { value: 'Billing & Payments', labelKey: 'support.topicBilling' },
  { value: 'Campaign Issues', labelKey: 'support.topicCampaigns' },
  { value: 'Reports & Analytics', labelKey: 'support.topicReports' },
  { value: 'Bug Report', labelKey: 'support.topicBug' },
  { value: 'Feature Request', labelKey: 'support.topicFeature' },
  { value: 'Other', labelKey: 'support.topicOther' },
];

const SupportModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [topic, setTopic] = useState(TOPICS[0].value);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const reset = () => {
    setTopic(TOPICS[0].value);
    setSubject('');
    setBody('');
    setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setSending(true);
    setResult(null);
    try {
      // Same-origin call; backend forwards to Sense Support with a fresh
      // JWT. Avoids CORS and keeps the shared secret server-side.
      const resp = await api.post('/api/support/tickets', {
        subject: `[${topic}] ${subject.trim()}`,
        body: body.trim() || undefined,
      });
      const data = resp.data;
      if (data?.ok) {
        setResult({ ok: true, message: t('support.ticketCreated', { number: data.number }) });
        setSubject('');
        setBody('');
      } else {
        setResult({ ok: false, message: t('support.errUnexpected') });
      }
    } catch (err: any) {
      setResult({ ok: false, message: err?.response?.data?.detail || err?.message || t('common.networkShort') });
    } finally {
      setSending(false);
    }
  };

  // Single inline-styled component — avoids new CSS file and the
  // global .modal-overlay clash that bit us earlier.
  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(15, 18, 28, 0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'sense-fade-in 160ms ease',
      }}
    >
      <style>{`
        @keyframes sense-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes sense-rise-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
        .smdl-field { display: flex; flex-direction: column; gap: 6px; }
        .smdl-label { font-size: 12px; font-weight: 600; color: #4b5563; letter-spacing: 0.02em; text-transform: uppercase; }
        .smdl-input, .smdl-textarea, .smdl-select {
          width: 100%; box-sizing: border-box;
          padding: 11px 13px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          font-size: 14px; font-family: inherit; color: #111827;
          transition: border-color 120ms, background 120ms, box-shadow 120ms;
        }
        .smdl-textarea { resize: vertical; min-height: 96px; }
        .smdl-input:focus, .smdl-textarea:focus, .smdl-select:focus {
          outline: none; background: #fff;
          border-color: #E94B6C;
          box-shadow: 0 0 0 3px rgba(233, 75, 108, 0.12);
        }
        .smdl-btn-primary {
          padding: 11px 22px;
          background: linear-gradient(135deg, #FF6B4A 0%, #E94B6C 50%, #8E3A9A 100%);
          color: #fff; border: none; border-radius: 10px;
          font-size: 14px; font-weight: 600; cursor: pointer;
          transition: transform 120ms, box-shadow 120ms, opacity 120ms;
          box-shadow: 0 6px 18px rgba(233, 75, 108, 0.28);
        }
        .smdl-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(233, 75, 108, 0.35); }
        .smdl-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
        .smdl-btn-ghost {
          padding: 11px 18px; background: transparent; color: #6b7280;
          border: none; border-radius: 10px; font-size: 14px; cursor: pointer;
          transition: background 120ms;
        }
        .smdl-btn-ghost:hover { background: #f3f4f6; color: #111827; }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 92vw)',
          background: '#fff',
          borderRadius: 18,
          padding: 0,
          boxShadow: '0 20px 60px rgba(15, 18, 28, 0.25)',
          overflow: 'hidden',
          animation: 'sense-rise-in 200ms ease',
        }}
      >
        {/* Gradient header */}
        <div
          className="smdl-header"
          style={{
            background: 'linear-gradient(135deg, #FF6B4A 0%, #E94B6C 50%, #8E3A9A 100%)',
            padding: '20px 26px',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              className="smdl-icon"
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 8h8v2H8V8zm5 5H8v-2h5v2z"/>
              </svg>
            </div>
            <div>
              <div className="smdl-title" style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>{t('support.needHelp')}</div>
              <div className="smdl-subtitle" style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{t('support.replyByEmail')}</div>
            </div>
          </div>
          <button
            onClick={handleClose}
            aria-label={t('common.close')}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
              width: 30, height: 30, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 16, lineHeight: 1,
              transition: 'background 120ms',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.28)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.15)'; }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '22px 26px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="smdl-field">
            <label className="smdl-label" htmlFor="smdl-topic">{t('support.topic')}</label>
            <select
              id="smdl-topic"
              className="smdl-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            >
              {TOPICS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
            </select>
          </div>

          <div className="smdl-field">
            <label className="smdl-label" htmlFor="smdl-subject">{t('support.subject')}</label>
            <input
              id="smdl-subject"
              className="smdl-input"
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('support.subjectPh')}
            />
          </div>

          <div className="smdl-field">
            <label className="smdl-label" htmlFor="smdl-body">{t('support.description')}</label>
            <textarea
              id="smdl-body"
              className="smdl-textarea"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('support.descriptionPh')}
            />
          </div>

          {result && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 10,
                fontSize: 13,
                background: result.ok ? '#ecfdf5' : '#fef2f2',
                color: result.ok ? '#065f46' : '#991b1b',
                border: `1px solid ${result.ok ? '#a7f3d0' : '#fecaca'}`,
              }}
            >
              {result.message}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" className="smdl-btn-ghost" onClick={handleClose}>{t('common.cancel')}</button>
            <button type="submit" className="smdl-btn-primary" disabled={sending || !subject.trim()}>
              {t(sending ? 'support.sending' : 'support.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportModal;
